import { useEffect } from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { isString } from 'lodash';

import { makeRequest, removeEmptyKeys } from '@app/utils';
import {
  INVOKE_PATH_PREFIX,
  ROUTE,
  SSO_REDIRECT_ROUTE_KEY,
} from '@app/constants';

import { useQueryParams } from '../useQueryParams';

interface SSOLoginDto {
  location: string;
  bindingMethod: 'POST' | 'GET';
  relayState: string;
  samlRequest: string;
}

interface SamlCheckDto {
  active: boolean;
}

interface SSOLoginProps {
  institutionId?: string;
}

type SSOMutation = UseMutationResult<void, unknown, SSOLoginProps>;

const processSSOLoginResponse = (
  response: SSOLoginDto,
  redirectRoute: string
) => {
  if (redirectRoute) {
    sessionStorage.setItem(SSO_REDIRECT_ROUTE_KEY, redirectRoute);
  }

  if (response.bindingMethod === 'POST') {
    const form = document.getElementById('ssoForm') as HTMLFormElement;

    form.setAttribute('action', response.location);
    form.setAttribute('method', response.bindingMethod);

    const samlRequest = document.createElement('input');

    samlRequest.setAttribute('type', 'hidden');
    samlRequest.setAttribute('name', 'SAMLRequest');
    samlRequest.setAttribute('data-auto', 'samlRequestInput');
    samlRequest.setAttribute('value', response.samlRequest);
    form.appendChild(samlRequest);

    const relayState = document.createElement('input');

    relayState.setAttribute('type', 'hidden');
    relayState.setAttribute('name', 'RelayState');
    relayState.setAttribute('data-auto', 'relayStateInput');
    relayState.setAttribute('value', response.relayState);
    form.appendChild(relayState);

    form.submit();
  } else {
    window.open(response.location, '_self');
  }
};

export const useSSOLogin = ({
  shouldCheckSSO = false,
  setFormError,
  getFormErrorMessage,
}: {
  shouldCheckSSO?: boolean;
  setFormError: (error: string) => void;
  getFormErrorMessage: (error: string) => string;
}): {
  samlCheckQuery: UseQueryResult<SamlCheckDto>;
  ssoMutation: SSOMutation;
} => {
  const { redirect } = useQueryParams();

  const redirectRoute = isString(redirect) ? redirect : ROUTE.search;

  const getRedirectRoute = () => {
    return `${window.origin}${redirectRoute}`;
  };

  const samlCheckQuery = useQuery(
    ['samlCheck'],
    () => makeRequest<SamlCheckDto>({ url: '/saml/check' }),
    { enabled: shouldCheckSSO }
  );

  const ssoMutation = useMutation(
    ['ssoLogin'],
    ({ institutionId }: SSOLoginProps = {}) =>
      makeRequest<SSOLoginDto>({
        url: `${INVOKE_PATH_PREFIX}/saml/login`,
        method: 'POST',
        data: removeEmptyKeys({
          stripesUrl: getRedirectRoute(),
          institutionId,
        }),
        withCredentials: true,
      }).then(response => processSSOLoginResponse(response, redirectRoute))
  );

  useEffect(() => {
    if (ssoMutation.isError) {
      setFormError(getFormErrorMessage(ssoMutation.error as string));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoMutation.isError]);

  return {
    samlCheckQuery,
    ssoMutation,
  };
};
